/**
 * The four states every fetched screen can be in, as a pure function.
 *
 * Loading, error and empty are not decorations on a happy path - they are the
 * states an operator on a warehouse wifi spends most of a bad day in. Keeping the
 * classification here, away from React, means each one is asserted by a test that
 * needs no DOM, and means every screen reaches the same verdict from the same
 * rule instead of each one inventing its own spinner.
 *
 * `empty` is separated from `ready` deliberately. A pick list with zero lines is
 * not a broken screen and must never render as one; it is a finished round, and
 * the copy for it says so.
 */
export type ResourceStatus = 'loading' | 'error' | 'empty' | 'ready'

export interface ResourceState<T> {
  status: ResourceStatus
  data: T | null
  /** The `error` code from the Worker envelope, never a stack trace. */
  errorCode: string | null
  /** True while a refetch runs over data already on screen (no flash of spinner). */
  refreshing: boolean
}

export function loading<T>(over: Partial<ResourceState<T>> = {}): ResourceState<T> {
  return { status: 'loading', data: null, errorCode: null, refreshing: false, ...over }
}

/**
 * `isEmpty` is supplied by the caller because emptiness is domain-shaped: a cart
 * with no lines is empty, a credit balance of zero is not.
 */
export function settle<T>(data: T, isEmpty?: (d: T) => boolean): ResourceState<T> {
  return {
    status: isEmpty?.(data) ? 'empty' : 'ready',
    data,
    errorCode: null,
    refreshing: false,
  }
}

export function failed<T>(errorCode: string, keep: T | null = null): ResourceState<T> {
  return { status: 'error', data: keep, errorCode, refreshing: false }
}

/** A retry keeps whatever is on screen, so a flaky network does not blank the work. */
export function retrying<T>(previous: ResourceState<T>): ResourceState<T> {
  return previous.data === null
    ? loading<T>()
    : { status: previous.status === 'error' ? 'ready' : previous.status, data: previous.data, errorCode: null, refreshing: true }
}

/** Whether a status should be announced to a screen reader as a change of state. */
export function isTerminal(status: ResourceStatus): boolean {
  return status !== 'loading'
}

/**
 * The one place that turns a status into words. Copy lives with the rule so a new
 * screen cannot ship "Something went wrong".
 */
export function emptyCopy(kind: string): string {
  return `Nothing here yet - ${kind}`
}

export function errorCopy(code: string | null): string {
  switch (code) {
    case 'FORBIDDEN':
      return 'You do not have access to this. Ask Mitali.'
    case 'UNAUTHENTICATED':
      return 'Your session expired. Sign in again.'
    case 'offline':
      return 'No connection. Your work is saved on this device and will sync.'
    default:
      return `Could not load this. Tap retry.${code ? ` (${code})` : ''}`
  }
}
